import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { printQuote } from "@/lib/printQuote";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Check, X, Phone, CheckCircle2, Loader2, ShieldCheck, Printer } from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";

const RESULT = {
  accettato: { icon: CheckCircle2, color: "#10B981", title: "Preventivo accettato", msg: "Grazie! Abbiamo registrato la tua accettazione. Ti contatteremo a breve per organizzare i prossimi passi." },
  rifiutato: { icon: X, color: "#EF4444", title: "Preventivo rifiutato", msg: "Abbiamo registrato la tua risposta. Se cambi idea o desideri modifiche, contattaci pure." },
  contatto_richiesto: { icon: Phone, color: "#F59E0B", title: "Richiesta inviata", msg: "Grazie! Un membro dello staff PT-MED ti contatterà al più presto." },
};

export default function PublicPreventivo() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [contact, setContact] = useState({ telefono: "", messaggio: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/public/quotes/${token}`)
      .then(({ data }) => {
        setQuote(data);
        if (["accettato", "rifiutato", "contatto_richiesto"].includes(data.stato)) setResult(data.stato);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  const respond = async (azione, extra = {}) => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/quotes/${token}/respond`, { azione, ...extra });
      setResult(data.stato);
      setContactOpen(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#16212B]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (error || !quote) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#16212B] text-center p-6">
        <img src="/ptmed-logo-dark.jpg" alt="PT-MED" className="h-16 w-16 rounded-xl object-cover mb-6" />
        <h1 className="font-heading text-2xl font-bold text-white">Preventivo non disponibile</h1>
        <p className="text-slate-400 mt-2">Il link potrebbe non essere valido o il preventivo non è più attivo.</p>
      </div>
    );
  }

  const R = result ? RESULT[result] : null;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-[#16212B] px-6 py-8">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <img src="/ptmed-logo-dark.jpg" alt="PT-MED" className="h-14 w-14 rounded-xl object-cover ring-1 ring-white/10" />
          <div>
            <h1 className="font-heading text-xl font-bold text-white">PT-MED</h1>
            <p className="text-xs text-slate-400">{quote.studio?.sottotitolo}</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-6 sm:p-8">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
              <div>
                <p className="text-xs uppercase tracking-widest text-primary font-semibold">Preventivo</p>
                <h2 className="font-heading text-2xl font-bold text-slate-900 font-mono-data">{quote.numero}</h2>
              </div>
              <div className="text-right text-sm text-slate-500">
                <p>Data: {quote.created_at ? format(new Date(quote.created_at), "d MMMM yyyy", { locale: it }) : "—"}</p>
                {quote.validita && <p>Valido fino al: {format(new Date(quote.validita), "d MMMM yyyy", { locale: it })}</p>}
                <Button variant="outline" size="sm" onClick={() => printQuote(quote)} data-testid="public-print-btn" className="mt-2">
                  <Printer className="mr-1.5 h-4 w-4" /> Stampa / Salva PDF
                </Button>
              </div>
            </div>

            <p className="text-sm text-slate-500 mb-1">Preparato per</p>
            <p className="font-semibold text-slate-800 mb-6">{quote.cliente_nome}</p>

            {/* Line items */}
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-2.5 font-semibold">Descrizione</th>
                    <th className="px-2 py-2.5 font-semibold text-center">Qtà</th>
                    <th className="px-2 py-2.5 font-semibold text-right">Prezzo</th>
                    <th className="px-4 py-2.5 font-semibold text-right">Totale</th>
                  </tr>
                </thead>
                <tbody>
                  {quote.righe.map((r, i) => {
                    let line = (r.quantita || 0) * (r.prezzo_unitario || 0);
                    line -= line * ((r.sconto_perc || 0) / 100);
                    return (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-4 py-3 text-slate-800">
                          {r.descrizione}
                          {r.sconto_perc > 0 && <span className="ml-2 text-xs text-red-500">−{r.sconto_perc}%</span>}
                        </td>
                        <td className="px-2 py-3 text-center font-mono-data">{r.quantita}</td>
                        <td className="px-2 py-3 text-right font-mono-data">€ {(r.prezzo_unitario || 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-right font-mono-data font-medium">€ {line.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="mt-4 ml-auto max-w-xs space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-500"><span>Subtotale</span><span className="font-mono-data">€ {(quote.subtotale || 0).toFixed(2)}</span></div>
              {quote.sconto_totale > 0 && (
                <div className="flex justify-between text-red-600"><span>Sconto {quote.sconto_globale_perc}%</span><span className="font-mono-data">− € {(quote.sconto_totale || 0).toFixed(2)}</span></div>
              )}
              <div className="flex justify-between border-t pt-2 text-lg font-bold text-slate-900"><span>Totale</span><span className="font-mono-data text-primary">€ {(quote.totale || 0).toFixed(2)}</span></div>
            </div>

            {(quote.modalita_pagamento || quote.metodo_pagamento) && (
              <p className="mt-4 text-xs text-slate-500">
                Pagamento: {[quote.modalita_pagamento, quote.metodo_pagamento].filter(Boolean).join(" · ")}
              </p>
            )}
            {quote.note && <p className="mt-4 rounded-lg bg-slate-50 p-3 text-sm text-slate-600 whitespace-pre-line">{quote.note}</p>}
            {quote.condizioni && (
              <div className="mt-6 border-t border-slate-200 pt-5" data-testid="quote-condizioni-public">
                <h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-slate-500 mb-2">Condizioni</h3>
                <p className="text-xs leading-relaxed text-slate-600 whitespace-pre-line">{quote.condizioni}</p>
              </div>
            )}
          </div>

          {/* Action / result footer */}
          <div className="border-t border-slate-200 bg-slate-50 p-6 sm:p-8">
            {R ? (
              <div className="flex flex-col items-center text-center py-4" data-testid="quote-result">
                <div className="h-14 w-14 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: `${R.color}1a` }}>
                  <R.icon className="h-7 w-7" style={{ color: R.color }} />
                </div>
                <h3 className="font-heading text-lg font-bold text-slate-900">{R.title}</h3>
                <p className="text-sm text-slate-500 mt-1 max-w-md">{R.msg}</p>
              </div>
            ) : (
              <>
                <p className="text-center text-sm text-slate-600 mb-4">Come desideri procedere con questo preventivo?</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Button onClick={() => respond("accetta")} disabled={submitting} data-testid="quote-accept-btn"
                          className="h-12 bg-emerald-600 hover:bg-emerald-700 font-semibold">
                    <Check className="mr-2 h-5 w-5" /> Accetta
                  </Button>
                  <Button onClick={() => setContactOpen(true)} disabled={submitting} variant="outline" data-testid="quote-contact-btn"
                          className="h-12 border-amber-300 text-amber-700 hover:bg-amber-50 font-semibold">
                    <Phone className="mr-2 h-5 w-5" /> Voglio essere contattato
                  </Button>
                  <Button onClick={() => respond("rifiuta")} disabled={submitting} variant="outline" data-testid="quote-reject-btn"
                          className="h-12 border-red-300 text-red-600 hover:bg-red-50 font-semibold">
                    <X className="mr-2 h-5 w-5" /> Rifiuta
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>

        <p className="flex items-center justify-center gap-1.5 text-center text-xs text-slate-400">
          <ShieldCheck className="h-3.5 w-3.5" /> Preventivo generato da PT-MED · La tua risposta viene inviata in modo sicuro
        </p>
      </div>

      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Richiedi di essere contattato</DialogTitle>
            <DialogDescription>Lasciaci un recapito e ti richiameremo per parlarne insieme.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Telefono</Label>
              <Input value={contact.telefono} onChange={(e) => setContact((c) => ({ ...c, telefono: e.target.value }))}
                     placeholder="Il tuo numero" data-testid="contact-phone-input" className="mt-1.5 h-11" />
            </div>
            <div>
              <Label>Messaggio (opzionale)</Label>
              <Textarea value={contact.messaggio} onChange={(e) => setContact((c) => ({ ...c, messaggio: e.target.value }))}
                        rows={3} data-testid="contact-message-input" className="mt-1.5" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactOpen(false)}>Annulla</Button>
            <Button onClick={() => respond("contatto", contact)} disabled={submitting} data-testid="contact-submit-btn" className="font-semibold">
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Invia richiesta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
